import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import {
  fetchMyNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const STAGE_ICONS = {
  'Pending Team Assignment': { icon: 'Users', color: 'text-warning' },
  'Calculated': { icon: 'Calculator', color: 'text-blue-400' },
  'Pending Approval': { icon: 'Clock', color: 'text-orange-400' },
  'Approved': { icon: 'CheckCircle', color: 'text-success' },
  'Ready for Payment': { icon: 'CreditCard', color: 'text-purple-400' },
  'Paid': { icon: 'Banknote', color: 'text-emerald-400' },
  'Rejected': { icon: 'XCircle', color: 'text-error' },
  'On Hold': { icon: 'PauseCircle', color: 'text-yellow-400' },
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const NotificationBell = ({ persistentBadge = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const channelRef = useRef(null);

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;
  const showBadge = unreadCount > 0 || persistentBadge;
  const badgeCount = unreadCount > 0 ? unreadCount : null;

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await fetchMyNotifications({ limit: 30 });
      setNotifications(data || []);
    } catch (err) {
      console.warn('[NotificationBell] Load error:', err?.message);
    }
  }, [user?.id]);

  // Initial load
  useEffect(() => {
    if (!user?.id) return;
    loadNotifications();
  }, [loadNotifications, user?.id]);

  // Supabase Realtime subscription — replaces polling for instant updates
  useEffect(() => {
    if (!user?.id) return;

    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }

    const channel = supabase
      ?.channel(`bell-notifications-${user?.id}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'incentive_notifications',
          filter: `recipient_user_id=eq.${user?.id}`,
        },
        (payload) => {
          const newNotification = payload?.new;
          if (!newNotification) return;
          // Prepend new notification to the list
          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'incentive_notifications',
          filter: `recipient_user_id=eq.${user?.id}`,
        },
        (payload) => {
          const updated = payload?.new;
          if (!updated) return;
          setNotifications(prev =>
            prev?.map(n => n?.id === updated?.id ? { ...n, ...updated } : n)
          );
        }
      )
      ?.subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [user?.id]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = async () => {
    setOpen(o => !o);
    if (!open) {
      setLoading(true);
      await loadNotifications();
      setLoading(false);
    }
  };

  const handleMarkRead = async (e, id) => {
    e?.stopPropagation();
    await markNotificationRead(id);
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, is_read: true } : n));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
  };

  const handleDelete = async (e, id) => {
    e?.stopPropagation();
    await deleteNotification(id);
    setNotifications(prev => prev?.filter(n => n?.id !== id));
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await markNotificationRead(notification?.id);
      setNotifications(prev => prev?.map(n => n?.id === notification?.id ? { ...n, is_read: true } : n));
    }
    if (notification?.action_url) {
      navigate(notification?.action_url);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={18} className="text-foreground" />
        {showBadge && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-bold leading-none">
            {badgeCount !== null ? (badgeCount > 99 ? '99+' : badgeCount) : '!'}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={16} className="text-primary" />
              <span className="font-semibold text-sm text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-error/15 text-error text-xs font-semibold">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary hover:underline transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-[420px] scrollbar-custom">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="p-3 rounded-full bg-muted/50">
                  <Icon name="BellOff" size={24} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications?.map(notification => {
                const stageConfig = STAGE_ICONS?.[notification?.stage] || { icon: 'Bell', color: 'text-primary' };
                return (
                  <div
                    key={notification?.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-border/50 last:border-0 hover:bg-muted/40 ${!notification?.is_read ? 'bg-primary/5' : ''}`}
                  >
                    {/* Stage Icon */}
                    <div className={`flex-shrink-0 mt-0.5 p-2 rounded-xl bg-muted/50 ${stageConfig?.color}`}>
                      <Icon name={stageConfig?.icon} size={14} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs leading-relaxed ${!notification?.is_read ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {notification?.message}
                        </p>
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!notification?.is_read && (
                            <button
                              onClick={(e) => handleMarkRead(e, notification?.id)}
                              className="p-1 rounded hover:bg-muted transition-colors"
                              title="Mark as read"
                            >
                              <Icon name="Check" size={12} className="text-success" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(e, notification?.id)}
                            className="p-1 rounded hover:bg-muted transition-colors"
                            title="Dismiss"
                          >
                            <Icon name="X" size={12} className="text-muted-foreground" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground font-mono">{notification?.ticket_number}</span>
                        <span className="text-[10px] text-muted-foreground">·</span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(notification?.created_at)}</span>
                        {!notification?.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </div>
                      {notification?.action_label && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-primary hover:underline">
                          {notification?.action_label}
                          <Icon name="ArrowRight" size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="px-4 py-2.5 border-t border-border bg-muted/20">
              <button
                onClick={() => { navigate('/notification-history'); setOpen(false); }}
                className="w-full text-center text-xs text-primary hover:underline transition-colors"
              >
                View notification history →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
